import {

    IconMessageCircle,
    IconMessageCircleFilled,
    IconSquareRoundedPlus,
    IconSquareRoundedPlusFilled,
    IconTag,
    IconTagFilled,
    IconUser,
    IconUserFilled,
} from "@tabler/icons-react";
import { memo, ReactNode } from "react";
import { NavLink } from "react-router-dom";
import {
    ROUTE_ADD_ITEM,
    ROUTE_CHAT,
    ROUTE_SETTING,
    ROUTE_SHOP,
} from "../../../const/routes";
import { useKeyboardVisible } from "../../../hooks/useKeyboardVisible";
import ChatUnread from "../../chat/core/ChatUnread.tsx";
import Authorized from "../../core/auth/Authorized.tsx";
import DockButton from "./DockButton";

const ICON_SHOP = <IconTag stroke={2} />;
const ICON_SHOP_ACTIVE = <IconTagFilled />;
const ShopButton = memo(function ShopButton({
  isActive,
}: {
  isActive: boolean;
}) {
  return (
    <DockButton
      isActive={isActive}
      label="anzeigen"
      icon={ICON_SHOP}
      iconActive={ICON_SHOP_ACTIVE}
    />
  );
});

const ICON_ADD = <IconSquareRoundedPlus stroke={2} />;
const ICON_ADD_ACTIVE = <IconSquareRoundedPlusFilled />;
const AddButton = memo(function AddButton({ isActive }: { isActive: boolean }) {
  return (
    <DockButton
      label="neu"
      isActive={isActive}
      icon={ICON_ADD}
      iconActive={ICON_ADD_ACTIVE}
    />
  );
});

const ICON_CHAT = <IconMessageCircle stroke={2} />;
const ICON_CHAT_ACTIVE = <IconMessageCircleFilled />;
const CHAT_UNREAD = (
  <Authorized>
    <div className="absolute top-0 -translate-y-1 translate-x-3">
      <ChatUnread />
    </div>
  </Authorized>
);
const ChatButton = memo(function ChatButton({ active }: { active: boolean }) {
  return (
    <DockButton
      label="chat"
      isActive={active}
      icon={ICON_CHAT}
      iconActive={ICON_CHAT_ACTIVE}
    >
      {CHAT_UNREAD}
    </DockButton>
  );
});

const ICON_MORE = <IconUser stroke={2} />;
const ICON_MORE_ACTIVE = <IconUserFilled />;
const MoreButton = memo(function MoreButton({ active }: { active: boolean }) {
  return (
    <DockButton
      label="profil"
      isActive={active}
      icon={ICON_MORE}
      iconActive={ICON_MORE_ACTIVE}
    />
  );
});

// Memoized dock item to prevent unnecessary re-renders
const DockItem = memo(
  ({
    to,
    children,
  }: {
    to: string;
    children: (isActive: boolean) => ReactNode;
  }) => {
    return (
      <NavLink
        role="button"
        to={to}
        className={({ isActive }) =>
          isActive ? "dock-active" : "dock-default active:dock-active"
        }
        style={{
          WebkitTapHighlightColor: "transparent",
          touchAction: "manipulation",
        }}
      >
        {({ isActive, isPending }) => children(isActive || isPending)}
      </NavLink>
    );
  },
);
DockItem.displayName = "DockItem";

const Dock = () => {
  const keyboardVisible = useKeyboardVisible();

  if (keyboardVisible) return null;

  return (
    <div
      className="dock dock-md lg:hidden z-10"
      style={{
        touchAction: "manipulation",
      }}
    >

      <DockItem to={ROUTE_SHOP()}>
        {(isActive) => <ShopButton isActive={isActive} />}
      </DockItem>

      <DockItem to={ROUTE_ADD_ITEM()}>
        {(isActive) => <AddButton isActive={isActive} />}
      </DockItem>

      <DockItem to={ROUTE_CHAT()}>
        {(isActive) => <ChatButton active={isActive} />}
      </DockItem>

      <DockItem to={ROUTE_SETTING}>
        {(isActive) => <MoreButton active={isActive} />}
      </DockItem>
    </div>
  );
};

export default Dock;
